import { useEffect, useState } from 'react';
import { formatElapsed } from '../utils/time';

// How often displayed spans are recomputed. Fine-grained enough that a "Xs"
// reading is never badly wrong, coarse enough to be free on a large fleet.
const TICK_MS = 15000;

// One interval for the whole page rather than one per device row. Consumers
// subscribe on mount; the timer only runs while at least one is listening.
const subscribers = new Set();
let timerId = null;

function subscribe(fn) {
  subscribers.add(fn);
  if (timerId === null) {
    timerId = setInterval(() => {
      const now = Date.now();
      subscribers.forEach((notify) => notify(now));
    }, TICK_MS);
  }

  return () => {
    subscribers.delete(fn);
    if (subscribers.size === 0 && timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  };
}

/**
 * Live relative time for an ISO-8601 timestamp.
 *
 * The value is recomputed in the browser on a shared ticker rather than being
 * rendered once by the server. That matters for staleness: a pre-rendered
 * string freezes at whatever the poller last wrote, so a dead poller leaves the
 * column reading "0s" indefinitely. Computing client-side means the span keeps
 * growing and the staleness becomes visible.
 *
 * Returns 'Never' for a missing or unparseable timestamp, matching the
 * backend's format_relative_time(None).
 */
export default function useRelativeTime(iso) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => subscribe(setNow), []);

  if (!iso) return 'Never';

  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'Never';

  return formatElapsed(now - then);
}
